/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import type { CosmosButtonProps } from "@components/elements/atoms/button";
import type { CosmosVariableRichText } from "@components/elements/atoms/text";
import type {
	CosmosModButtonFragment,
	FaqItemElementFragment,
	FaqTabElementFragment,
	RefFaqItemElementFragment,
} from "@cosmos/hygraph-schema";
import { CosmosEnumFaqButtonsPosition } from "@cosmos/hygraph-schema";
import { cleanHashId, getFaqId, getShortId } from "./id.utils";

export type FAQHashFields = {
	fallbackLinkId: string;
	anchorLinkId?: string;
};

export type FAQItem = {
	id: string;
	question: CosmosVariableRichText;
	answer: CosmosVariableRichText;
	defaultOpen?: boolean;
} & FAQHashFields;

export type FAQTab = {
	id: string;
	title?: null | string;
	items: FAQItem[];
	firstItemDefaultOpen?: boolean;
} & FAQHashFields;

export type FAQProps = {
	id?: string;
	items?: FAQItem[];
	title?: string;
	titleVariant?: "headingXL" | "headingLG";
	buttons?: CosmosButtonProps[];
	buttonsPosition?: CosmosEnumFaqButtonsPosition;
	firstItemDefaultOpen?: boolean | null;
};

export const hasFAQHash = (item: FAQHashFields, hash: string): boolean =>
	(!!item?.anchorLinkId && item?.anchorLinkId === hash) || (!!item?.fallbackLinkId && item?.fallbackLinkId === hash);

export const cleanFAQItem = (item: FaqItemElementFragment): FAQItem => ({
	id: item?.id,
	anchorLinkId: cleanHashId(item?.anchorLinkId || "") || undefined,
	fallbackLinkId: getFaqId(getShortId(item?.id)),
	question: {
		...item?.question,
	},
	answer: {
		...item?.answer,
	},
});

export const cleanRefFAQItem = (item: RefFaqItemElementFragment): FAQItem => ({
	id: item?.id,
	anchorLinkId: cleanHashId(item?.anchorLinkId || "") || undefined,
	fallbackLinkId: getFaqId(getShortId(item?.id)),
	question: {
		html: item?.question || "",
	},
	answer: {
		html: item?.answer || "",
	},
});

export const processFAQItems = (
	items: FaqItemElementFragment[] = [],
	refFaqItems: RefFaqItemElementFragment[] = [],
): FAQItem[] => {
	return [...items.map(cleanFAQItem), ...refFaqItems.map(cleanRefFAQItem)];
};

export const cleanFAQTab = (tab: FaqTabElementFragment): FAQTab => {
	return {
		id: tab?.id,
		title: tab?.title,
		anchorLinkId: cleanHashId(tab?.anchorLinkId || "") || undefined,
		fallbackLinkId: getFaqId(getShortId(tab?.id)),
		items: processFAQItems(tab.items, tab.refFaqItems),
		firstItemDefaultOpen: typeof tab?.firstItemDefaultOpen === "boolean" ? tab?.firstItemDefaultOpen : undefined,
	};
};

export type FAQButtonInput = Omit<CosmosModButtonFragment, "__typename">;

export const mapFAQButtons = (
	buttons: FAQButtonInput[] | null | undefined,
	component: string = "FAQ",
	section: string = "section",
): CosmosButtonProps[] => {
	if (!buttons) return [];

	return buttons.map((button): CosmosButtonProps => {
		return {
			...button,
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component,
					section,
				},
			},
		};
	});
};
